/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef } from "react";
import { useOutletContext } from "react-router-dom";
import { Sidebar } from "../../../shared";
import { Toast } from "primereact/toast";
import { Card } from "primereact/card";
import { Skeleton } from "primereact/skeleton";
import {
  Boton,
  Titulo,
  Formulario,
  accessAPI
} from "../../../shared/index.js";
import TarjetaProductos from "./TarjetaProductos.js";

/**
 * Sección "Productos" del proceso. Es dueña del estado de la lista de
 * productos y del sidebar de "Agregar producto". TarjetaProductos sólo
 * muestra la tabla y maneja la edición/eliminación de productos
 * existentes.
 */
export default function ProcesoProductos() {
  const { proceso, loading } = useOutletContext();
  const [productos, setProductos] = useState(proceso?.producto || []);
  const [sidebarAgregar, setSidebarAgregar] = useState(false);
  const [loaderAgregar, setLoaderAgregar] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const toast = useRef(null);

  useEffect(() => {
    if (proceso?.producto) {
      setProductos(proceso.producto);
    }
  }, [proceso]);

  function handleAgregarProducto(contenidoCampos) {
    setLoaderAgregar(true);

    const camposPropios = {};
    const caracteristicasCriticas = {};

    for (const key in contenidoCampos) {
      const valor = contenidoCampos[key];
      const valorString = valor != null ? String(valor) : "";
      if (key.startsWith("cc_")) {
        caracteristicasCriticas[key.replace("cc_", "")] = valorString;
      } else {
        camposPropios[key] = valorString;
      }
    }

    accessAPI(
      "POST",
      `admin/proceso/producto/${proceso.id}`,
      { campospropios: camposPropios, caracteristicascriticas: caracteristicasCriticas },
      (respuesta) => {
        setLoaderAgregar(false);
        setSidebarAgregar(false);
        setFormKey((prev) => prev + 1);
        setProductos((prev) => [...prev, respuesta.productoParaDevolver]);
        toast.current?.show({
          severity: "success",
          summary: "Éxito",
          detail: "Producto agregado correctamente"
        });
      },
      (error) => {
        setLoaderAgregar(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: error[0]?.msg || "Error al agregar producto",
          sticky: true
        });
      }
    );
  }

  function generarFormConfigAgregar() {
    const config = {
      secciones: [],
      botones: [
        {
          etiqueta: "Agregar producto",
          verde: true,
          callback: handleAgregarProducto,
          validar: true
        }
      ]
    };

    // Sección: campos propios del producto
    if (proceso.certificadotipo?.productocampospropios?.length > 0) {
      const seccion = { nombre: "Campos del producto", campos: [] };

      for (const campoPropio of proceso.certificadotipo.productocampospropios) {
        const campo = {
          nombre: campoPropio.id.toString(),
          etiqueta: campoPropio.unidades
            ? `${campoPropio.etiqueta} (${campoPropio.unidades})`
            : campoPropio.etiqueta,
          notas: campoPropio.notas,
          requerido: !!campoPropio.obligatoriobool
        };

        switch (campoPropio.tipo) {
          case "int":
            campo.tipoitemformularioid = 4;
            break;
          case "float":
            campo.tipoitemformularioid = 8;
            break;
          default:
            campo.tipoitemformularioid = 1;
            break;
        }

        if (campoPropio.opcionesdropdown?.length) {
          campo.opciones = campoPropio.opcionesdropdown;
          campo.sinOpcionInicial = true;
          campo.tipoitemformularioid = 5;
        }

        if (campoPropio.autocompletarScript) {
          campo.disabled = true;
          // Texto, aunque el campo sea numérico: el cartel es un string y un
          // InputNumber lo muestra como NaN. El tipo guardado ya se fuerza a
          // string al cargar el script, pero se puede volver a cambiar desde
          // la edición del campo, así que acá no se depende de eso.
          campo.tipoitemformularioid = 1;
          campo.respuesta = "Autocompletado por script";
        }

        seccion.campos.push(campo);
      }

      config.secciones.push(seccion);
    }

    // Sección: características críticas
    if (
      proceso.certificadotipo?.ProductoCaracteristicasCriticas?.length > 0
    ) {
      const seccion = { nombre: "Características críticas", campos: [] };
      for (const caracteristica of proceso.certificadotipo
        .ProductoCaracteristicasCriticas) {
        seccion.campos.push({
          nombre: `cc_${caracteristica.id}`,
          etiqueta: caracteristica.etiqueta,
          notas: caracteristica.notas,
          tipoitemformularioid: 1,
          requerido: false
        });
      }
      config.secciones.push(seccion);
    }

    return config;
  }

  if (loading || !proceso) {
    return (
      <div>
        <div
          css={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: "2em",
            marginBottom: "1em"
          }}
        >
          <Skeleton width="20rem" height="2rem" />
          <Skeleton width="10rem" height="2.5rem" />
        </div>
        <Card>
          <Skeleton
            width="100%"
            height="2.5rem"
            css={{ marginBottom: "0.5em" }}
          />
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton
              key={i}
              width="100%"
              height="3rem"
              css={{ marginBottom: "0.3em" }}
            />
          ))}
        </Card>
      </div>
    );
  }

  return (
    <>
      <Toast ref={toast} position="top-center" />
      <Titulo
        urlVolver={`/clientes/${proceso.cliente.id}/procesos`}
        subtitulo={`${proceso.cliente.razonsocial} - ${proceso.nombrefantasia}`}
        botones={
          <Boton callback={() => setSidebarAgregar(true)}>
            Agregar producto
          </Boton>
        }
      >
        Proceso {proceso.nombre} - Productos
      </Titulo>

      <TarjetaProductos
        proceso={proceso}
        productos={productos}
        setProductos={setProductos}
      />

      <Sidebar
        visible={sidebarAgregar}
        onHide={() => setSidebarAgregar(false)}
        position="right"
        className="w-full lg:w-30rem md:w-30rem"
      >
        <Titulo>Agregar producto</Titulo>
        <Formulario
          key={formKey}
          config={generarFormConfigAgregar()}
          loader={loaderAgregar}
        />
      </Sidebar>
    </>
  );
}
